import trace from '~/lib/trace';
import React, {useState, useEffect, useMemo, useContext} from 'react';
import PropTypes from 'prop-types';
import {useNavigation} from '@react-navigation/native';
import moment from 'moment';
import pickBy from 'lodash/pickBy';
import LogRocket from 'logrocket';

import UserContext from '~/components/context/UserContext';
import {
  service_request as serviceRequestApi,
  rob as robApi,
  car as carApi,
  fleet as fleetApi,
} from '~/api/private';
import {useDispatch, useSelector, useThrottledCallback} from '~/lib/hooks';
import {setAppDoneLoading, setAppIsLoading} from '~/actions';
import {
  isReadOnlyStatus,
  shouldRedirectToCalendar,
  deriveRequestVehicleFlags,
  isRequestEditable,
  shouldFetchRequestInvoice,
  shouldRefreshRequestDetails,
} from '../helper';
import ServiceRequestContext from '../context';

const extractOverviewRobCodes = (components, replacementSlaRobCode) => {
  const robCodes = [];

  for (const component of components ?? []) {
    for (const task of component.tasks ?? []) {
      if (task.rob_code) {
        robCodes.push(task.rob_code);
      }
    }
  }

  if (replacementSlaRobCode) {
    robCodes.push(replacementSlaRobCode);
  }

  return [...new Set(robCodes)];
};

const buildSlaOperations = (slaCodes) => {
  const operations = {};

  for (const item of slaCodes ?? []) {
    if (item.operations) {
      operations[item.rob_code] = item.operations;
    }
  }

  return operations;
};

const fetchInvoiceIfNeeded = async ({
  overview,
  request_id,
  is_fleet_car,
  is_retail_car,
  setInvoice,
}) => {
  if (
    shouldFetchRequestInvoice(
      overview.status,
      is_fleet_car,
      is_retail_car,
      overview.rob_invoicing_status,
    )
  ) {
    const {result: invoice} = await serviceRequestApi.invoice(request_id);
    setInvoice(invoice);
  }
};

const fetchContractAndComponents = async ({
  overview,
  request_id,
  is_fleet_car,
  setRobContract,
  setRobComponents,
}) => {
  if (is_fleet_car) {
    const {result: fleet_contract} = await fleetApi.contract(
      overview.fleet_contract_id,
    );

    setRobContract(fleet_contract.contract);
    return;
  }

  const rob_codes = extractOverviewRobCodes(
    overview.components,
    overview.replacement?.sla?.rob_code,
  );

  const contractResponse = await robApi.contract(overview.license, request_id);
  let robComponentsResponse = null;

  if (rob_codes.length) {
    robComponentsResponse = await robApi.components(
      overview.license,
      request_id,
      rob_codes,
    );
  }

  setRobContract(contractResponse.result);

  if (robComponentsResponse?.result) {
    setRobComponents(robComponentsResponse.result);
  }
};

const fetchCar = async ({license, setCar}) => {
  const car = await carApi.fetch(license);
  setCar(car);
};

const fetchSla = async ({request_id, setSlaKpis, setSlaOperations}) => {
  const {result: sla} = (await serviceRequestApi.sla(request_id)) ?? {};
  if (!sla) {
    return false;
  }

  setSlaKpis(sla?.kpis ?? {});
  setSlaOperations(buildSlaOperations(sla?.codes));
  return true;
};

const fetchHistory = async ({
  request_id,
  license,
  dealer_id,
  is_fleet_car,
  is_retail_car,
  setServiceHistory,
}) => {
  const {result: history = []} = await serviceRequestApi.history(request_id);
  const mergedHistory = [...history];

  if (!is_fleet_car && !is_retail_car) {
    const {result: robHistory} =
      (await robApi.history(dealer_id, license)) ?? {};

    if (robHistory?.open) {
      mergedHistory.push(
        ...robHistory.open.map((item) => ({
          ...item,
          date: moment(item.date, 'DD-MM-YYYY').toDate(),
          is_rob: true,
        })),
      );
    }

    if (robHistory?.archived) {
      mergedHistory.push(
        ...robHistory.archived.map((item) => ({
          ...item,
          date: moment(item.date, 'DD-MM-YYYY').toDate(),
          is_rob: true,
        })),
      );
    }
  }

  mergedHistory.sort((a, b) => new Date(b.date) - new Date(a.date));

  setServiceHistory(mergedHistory);
};

// What a refresh may re-fetch beyond the request overview (which is always
// re-read, since every derived flag hangs off it).
export const REFRESH_SCOPE = {
  invoice: 'invoice',
  contract: 'contract',
  car: 'car',
  sla: 'sla',
  history: 'history',
};

export const ALL_REFRESH_SCOPES = Object.values(REFRESH_SCOPE);

// Editing the request itself (tasks, tyres, SLA lines, mileage) cannot change
// the vehicle record or the service history of *other* requests, so those two
// are skipped. Anything that changes the request's status still refreshes
// everything, because history and invoicing do follow status.
export const REQUEST_EDIT_SCOPES = ALL_REFRESH_SCOPES.filter(
  (scope) => scope !== REFRESH_SCOPE.car && scope !== REFRESH_SCOPE.history,
);

const refreshRequestContextData = async ({
  request_id,
  dispatch,
  navigation,
  me,
  setOverview,
  setRobErrors,
  setVINPart,
  setVINNumber,
  setCurrentMileage,
  setReadLastMessage,
  setIsRetailCar,
  setIsFleetCar,
  setEditable,
  setReadOnly,
  setHasAutoline,
  setInvoice,
  setRobContract,
  setRobComponents,
  setCar,
  setSlaKpis,
  setSlaOperations,
  setServiceHistory,
  scopes = ALL_REFRESH_SCOPES,
}) => {
  const wants = (scope) => scopes.includes(scope);

  const {result} = await serviceRequestApi.get(request_id, dispatch);

  if (!result.length || shouldRedirectToCalendar(result[0])) {
    navigation.replace('Tabs', {
      screen: 'Calendar',
    });
    return false;
  }

  const overview = result[0];

  setOverview(overview);
  setRobErrors(overview.rob_errors);
  setVINPart(overview.vin_part);
  setVINNumber(overview.vin_number);
  setCurrentMileage(overview.mileage);
  setReadLastMessage(overview.last_message?.is_read ?? true);

  const {is_fleet_car: isFleetCar, is_retail_car: isRetailCar} =
    deriveRequestVehicleFlags(overview.request_type);
  setIsRetailCar(isRetailCar);
  setIsFleetCar(isFleetCar);

  setEditable(isRequestEditable(overview, isFleetCar, isRetailCar));
  setReadOnly(isReadOnlyStatus(overview.status));

  const dealer = me?.dealers.find((d) => d.id === overview.dealer_id);
  setHasAutoline(dealer?.package_access.includes('autoline'));

  await Promise.all(
    [
      wants(REFRESH_SCOPE.invoice) &&
        fetchInvoiceIfNeeded({
          overview,
          request_id,
          is_fleet_car: isFleetCar,
          is_retail_car: isRetailCar,
          setInvoice,
        }),
      wants(REFRESH_SCOPE.contract) &&
        fetchContractAndComponents({
          overview,
          request_id,
          is_fleet_car: isFleetCar,
          setRobContract,
          setRobComponents,
        }),
      wants(REFRESH_SCOPE.car) &&
        fetchCar({
          license: overview.license,
          setCar,
        }),
    ].filter(Boolean),
  );

  if (!wants(REFRESH_SCOPE.sla)) {
    return true;
  }

  // Fire and forget.
  fetchSla({
    request_id,
    setSlaKpis,
    setSlaOperations,
  })
    .then((result) => {
      if (!result || !wants(REFRESH_SCOPE.history)) {
        return;
      }

      return fetchHistory({
        request_id,
        license: overview.license,
        dealer_id: overview.dealer_id,
        is_fleet_car: isFleetCar,
        is_retail_car: isRetailCar,
        setServiceHistory,
      });
    })
    .catch(trace);

  return true;
};

const withServiceRequestContext = (Component) => {
  const WithServiceRequestContext = (props) => {
    const {request_id} = props.route.params ?? {};

    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [editable, setEditable] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [car, setCar] = useState(null);
    const [currentMileage, setCurrentMileage] = useState(null);
    const [robComponents, setRobComponents] = useState({});
    const [invoice, setInvoice] = useState(null);
    const [vinPart, setVINPart] = useState(null);
    const [vinNumber, setVINNumber] = useState(null);
    const [robContract, setRobContract] = useState(null);
    const [slaKpis, setSlaKpis] = useState({});
    const [slaOperations, setSlaOperations] = useState({});
    const [isFleetCar, setIsFleetCar] = useState(false);
    const [isRetailCar, setIsRetailCar] = useState(false);
    const [serviceHistory, setServiceHistory] = useState(null);
    const [hasAutoline, setHasAutoline] = useState(false);

    const [robErrors, setRobErrors] = useState([]);

    const [readLastMessage, setReadLastMessage] = useState(true);

    const dispatch = useDispatch();
    const forceRefresh = useSelector((state) => state.serviceRequestUpdate);
    const lastFetch = useSelector((state) => state.serviceRequestLastFetch);

    const navigation = useNavigation();

    const {me} = useContext(UserContext);

    const refresh = useThrottledCallback(
      async (scopes) => {
        try {
          return await refreshRequestContextData({
            scopes,
            request_id,
            dispatch,
            navigation,
            me,
            setOverview,
            setRobErrors,
            setVINPart,
            setVINNumber,
            setCurrentMileage,
            setReadLastMessage,
            setIsRetailCar,
            setIsFleetCar,
            setEditable,
            setReadOnly,
            setHasAutoline,
            setInvoice,
            setRobContract,
            setRobComponents,
            setCar,
            setSlaKpis,
            setSlaOperations,
            setServiceHistory,
          });
        } finally {
          setLoading(false);
        }
      },
      [navigation, request_id, me, dispatch],
    );

    useEffect(() => {
      if (overview) {
        LogRocket.track(
          'Service Request Details',
          pickBy({
            request_id,
            license: overview?.license,
            status: overview?.status,
            rob_status: overview?.rob?.status,
          }),
        );
      }
    }, [request_id, overview]);

    useEffect(() => {
      if (loading) {
        dispatch(setAppIsLoading());
      } else {
        dispatch(setAppDoneLoading());
      }
    }, [loading, dispatch]);

    useEffect(() => {
      const last_fetch_date = moment(lastFetch[request_id] ?? new Date(0));
      const update_date = moment(
        forceRefresh[request_id]?.timestamp ?? new Date(0),
      );
      if (shouldRefreshRequestDetails(last_fetch_date, update_date)) {
        refresh();
      }
    }, [request_id, forceRefresh, lastFetch, refresh]);

    const contextValue = useMemo(
      () => ({
        request_id,
        loading,
        overview,
        editable,
        read_only: readOnly,
        car,
        current_mileage: currentMileage,
        robComponents,
        invoice,
        vin_part: vinPart,
        vin_number: vinNumber,
        robContract,
        slaKpis,
        slaOperations,
        is_fleet_car: isFleetCar,
        is_retail_car: isRetailCar,
        robErrors,
        readLastMessage,
        serviceHistory,
        has_autoline: hasAutoline,
        refresh,
        setCar,
        setCurrentMileage,
        setRobContract,
        setRobErrors,
      }),
      [
        request_id,
        loading,
        overview,
        editable,
        readOnly,
        car,
        currentMileage,
        robComponents,
        invoice,
        vinPart,
        vinNumber,
        robContract,
        slaKpis,
        slaOperations,
        isFleetCar,
        isRetailCar,
        robErrors,
        readLastMessage,
        serviceHistory,
        hasAutoline,
        refresh,
        setCar,
        setCurrentMileage,
        setRobContract,
        setRobErrors,
      ],
    );

    return (
      <ServiceRequestContext.Provider value={contextValue}>
        <Component {...props} />
      </ServiceRequestContext.Provider>
    );
  };

  WithServiceRequestContext.propTypes = {
    route: PropTypes.shape({
      params: PropTypes.object,
    }),
  };

  return WithServiceRequestContext;
};

export default withServiceRequestContext;
